import React, {useEffect, useState} from 'react'
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Icon,
  IconButton,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
  VStack
} from '@chakra-ui/react'
import {Document, Page, pdfjs} from 'react-pdf'
import {useParams} from 'react-router-dom'
import {FaCheck, FaExclamation, FaMinus, FaPaperPlane, FaPlay, FaPlus, FaRedo, FaTrash} from 'react-icons/fa'
import {exportPdfRequest} from '../../api/exportDocumentPdf'
import {getDocument} from '../../api/getDocument'
import 'pdfjs-dist/build/pdf.worker.mjs'
import CostCenterDropDown from './CostCenterDropDown.jsx'
import ExpenseGroupsDropDown from './ExpenseGroupDropDown.jsx'
import {processDocument, updateDocument} from '../../api/ProcessDocument.js'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import ProjectsDropDown from './ProjectsDropDown.jsx'
import GroupsDropDown from './GroupsDropDown.jsx'
import {approveDocument} from '../../api/approveDocument.js'
import DocumentMetaData from './DocumentMetaData'

import {FastField, FieldArray, Formik} from 'formik'
import * as Yup from 'yup'
import {FaPencil} from 'react-icons/fa6'
import {getCompanySettings} from '../../../SettingsPage/api/getCompanySettings.js'
import {
  getArticles,
  getCostCenters,
  getExpenseGroups,
  getGroups,
  getProjects
} from '../../api/getNomenclatures.js'
import {sendDocumentToOMC} from "../../api/sendDocumentToOMC";
import VatDropDown from "./VatDropDown";
import {DOCUMENT_STATUS, LABEL, MESSAGE} from "../../../../constants";
import PaginatedDropdown from './PaginatedDropdown.jsx'
import PreviousDocumentsTable from "./PreviousDocumentsTable";
import DocumentDateField from './DocumentDateField.jsx'
import CaptureSetFieldValue from './CaptureSetFieldValue.jsx'
import { useAuth } from '../../../../auth/AuthProvider.js'
import PointOfWorksDropdown from './PointOfWorksDropdown.jsx'
import { DocumentLinesReadOnlyTable } from './DocumentLinesReadOnlyTable.jsx'


pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const formatDate = (timestamp) =>
  timestamp ? new Date(timestamp).toISOString().split('T')[0] : ''

const DocumentDetailsPage = () => {
  const {documentId} = useParams()
  const [document, setDocument] = useState(null)
  const [documentData, setDocumentData] = useState(null)
  const [pdfBlob, setPdfBlob] = useState(null)
  const [loadingPdf, setLoadingPdf] = useState(true)
  const [loadingData, setLoadingData] = useState(true)
  const [zoomLevel, setZoomLevel] = useState(1.0)
  const [numPages, setNumPages] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [status, setStatus] = useState(null)
  const [allowedMinDate, setAllowedMinDate] = useState(null)
  const [costCenters, setCostCenters] = useState([])
  const [loadingCostCenters, setLoadingCostCenters] = useState(true)
  const [expenseGroups, setExpenseGroups] = useState([])
  const [loadingExpenseGroups, setLoadingExpenseGroups] = useState(true)
  const [groups, setGroups] = useState([])
  const [loadingGroups, setLoadingGroups] = useState(true)
  const [projects, setProjects] = useState([])
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [showTotalDifferencesAlertBox, setShowTotalDifferencesAlertBox] = useState(false);
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [externalSetFieldValue, setExternalSetFieldValue] = useState(null);
  const [errorDescription, setErrorDescription] = useState(undefined)
  const [cif, setCif] = useState(null)
  const user=useAuth();
  const toast = useToast()

  const fetchData = async () => {
    try {
      setLoadingData(true);
      setLoadingPdf(true);
      setPdfBlob(null);
      const docResponse = await getDocument(documentId);
      const doc = docResponse.data;
      setCif(doc.cif)
      setDocument(doc || null);
      setStatus(doc?.status);
      let lines = [];
      let baseData = {};

      if (doc?.status?.includes('PENDING')) {
        const lines = doc.ocrResult.documentLines.map((line, i) => ({
          ...line,
          details: line.description,
          vatValue: line.vatPercentage !== undefined && line.vatPercentage !== null ? line.vatPercentage : 0,
          index: i + 1,
        }));

        baseData = {...doc.ocrResult, documentLines: lines};

        baseData.pointOfWorkTag = doc.data.pointOfWorkTag;
        baseData.notes = lines.reduce((accumulator, line) => accumulator.concat(line.details + '\n'),"")
      } else {
        lines = doc.data.documentLines.map((line, i) => ({
          ...line,
          index: i + 1,
        }));
        baseData = {...doc.data, documentLines: lines};
      }

      setDocumentData(baseData);

      const invoiceCif = baseData.organisation.cif;

      const normalizedInvoiceCif = invoiceCif.replace(/^RO/, '');

      const settingsResponse = await getCompanySettings();
      const settingsArray = settingsResponse;

      const companySetting = settingsArray.find((item) =>
        item.cif.includes(normalizedInvoiceCif)
      );
      let allowedMinDate = null;
      if (companySetting && companySetting.monthClosureTimestamp) {
        const closureDate = new Date(companySetting.monthClosureTimestamp);
        allowedMinDate = new Date(
          closureDate.getFullYear(),
          closureDate.getMonth() + 1,
          1,
          12,
          0,
          0
        );
        if(doc?.status !== DOCUMENT_STATUS.SENT_TO_OMC.key) {
            setAllowedMinDate(allowedMinDate);
        }

      }

      exportPdfRequest(documentId, true)
        .then((arrayBuffer) => {
          const blob = new Blob([arrayBuffer], {type: 'application/pdf'});
          setPdfBlob(blob);
          setLoadingPdf(false);
        })
        .catch((error) => {
          console.error('Error fetching PDF:', error);
          setLoadingPdf(false);
        });
    } catch (error) {
      console.error('Error fetching document data:', error);
    } finally {
      setLoadingData(false);
    }
  };


  const processAndSubmitDocument = async (values, actions) => {
    setProcessing(true);
    const updateDocumentRequest = {
      id: document.id,
      documentDate: values.documentDate ? values.documentDate : document.data.documentDate,
      details: values.details
        ? values.details.replace(/'/g, ' ')
        : (document.data.notes ? document.data.notes.replace(/'/g, ' ') : null),
      dueDate: values.dueDate ? values.dueDate : document.data.dueDate,
      pointOfWorkTag: values.pointOfWorkTag ? values.pointOfWorkTag : document.data.pointOfWorkTag,
      invoiceGroup: values.invoiceGroup !== undefined ? values.invoiceGroup : document.data.invoiceGroup,
      notes: values.notes ? values.notes : document.data.notes,

      documentLines: values.documentLines.map((line, i) => ({
        ...line,
        index: i + 1,
        details: line.details ? line.details.replace(/'/g, ' ') : null,
        invoicePeriodStartDate: line.invoicePeriodStartDate ? new Date(line.invoicePeriodStartDate).getTime() : null,
        invoicePeriodEndDate: line.invoicePeriodEndDate ? new Date(line.invoicePeriodEndDate).getTime() : null,
      })),
    }
    setShowValidationAlert(false);

    if (status?.includes('PENDING')) {
      processDocument(updateDocumentRequest).then((response) => {
        toast({
          title: 'Succes',
          description: MESSAGE.PROCESS.success,
          status: 'success',
          duration: 5000,
          isClosable: true
        });
        fetchData();
      })
        .catch((ignore) => {
          toast({
            title: 'Eroare',
            description: MESSAGE.PROCESS_ERROR.label,
            status: 'error',
            duration: 5000,
            isClosable: true
          });
        }).finally(() => {
        setProcessing(false);
        actions.setSubmitting(false);
      })
    } else {
      updateDocument(updateDocumentRequest).then((response) => {
        toast({
          title: 'Succes',
          description: MESSAGE.ACTUALIZARE.success,
          status: 'success',
          duration: 5000,
          isClosable: true
        });
        fetchData();
      })
        .catch((ignore) => {
          toast({
            title: 'Eroare',
            description: MESSAGE.UPDATE_ERROR.label,
            status: 'error',
            duration: 5000,
            isClosable: true
          });
        }).finally(() => {
        setProcessing(false);
        actions.setSubmitting(false);
      })
    }
  };

  useEffect(() => {
    if (documentId) {
      fetchData();
    }
  }, [documentId]);



  //  Zona call uri api pentru dropDown uri

  useEffect(() => {
    if (!cif) return;
    getCostCenters(cif)
      .then((response) => {
        setCostCenters(response)
        setLoadingCostCenters(false)
      })
      .catch((error) => {
        console.error('Eroare la preluarea datelor:', error)
        setLoadingCostCenters(false)
      })
  }, [cif])


  useEffect(() => {
    if (!cif) return;
    getExpenseGroups({
      cif,
      orderByList: [
        {
          field: 'name',
          direction: 'ASCENDING'
        }
      ]
    })
      .then((response) => {
        setExpenseGroups(response.sort((firstElement, secondElement) => firstElement.name.localeCompare(secondElement.name)))
        setLoadingExpenseGroups(false)
      })
      .catch((error) => {
        console.error('Eroare la preluarea datelor:', error)
        setLoadingExpenseGroups(false)
      })
  }, [cif])

  useEffect(() => {
    if (!cif) return;
    getGroups(cif)
      .then((response) => {
        setGroups(response)
        setLoadingGroups(false)
      })
      .catch((error) => {
        console.error('Error fetching articles:', error)
        setLoadingGroups(false)
      })
  }, [cif])

  useEffect(() => {
    if(!cif) return;
    getProjects({
      cif,
      orderByList: [
        {
          field: 'name',
          direction: 'ASCENDING'
        }
      ]
    })
      .then((response) => {
        setProjects(response)
        setLoadingProjects(false)
      })
      .catch((error) => {
        console.error('Eroare la preluarea datelor:', error)
        setLoadingProjects(false)
      })
  }, [cif])


  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.25, 3.0))
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.25, 0.5))
  const handleResetZoom = () => setZoomLevel(1.0)

  const handleApproveDocument = async () => {
    setProcessing(true)
    await approveDocument(documentId)
      .then((response) => {
        fetchData();
        setProcessing(false);
        toast({
          title: MESSAGE.APPROVE.successTitle,
          description: MESSAGE.APPROVE.textValue,
          status: 'success',
          duration: 5000,
          isClosable: true
        })
      }).catch((error) => {
        console.error(error);
        setProcessing(false);
        toast({
          title: 'Eroare',
          description: MESSAGE.APPROVE.error,
          status: 'error',
          duration: 5000,
          isClosable: true
        })
      })
  }
  const handleSendToOMC = async () => {
    setProcessing(true)
    await sendDocumentToOMC(documentId)
      .then((response) => {
        fetchData();
        setProcessing(false);
        toast({
          title: MESSAGE.SENT_TO_OMC.successTitle,
          description: MESSAGE.SENT_TO_OMC.textValue,
          status: 'success',
          duration: 5000,
          isClosable: true
        })
      }).catch((error) => {
        setErrorDescription(error?.response?.data?.metaData?.Err || MESSAGE.OMC_GENERIC_ERROR.label);
        setShowValidationAlert(true);
        console.log(error);
        setProcessing(false);
      })
  }

  const computeTotals = (lines) => {
    let totalFaraTva = 0
    let tvaTotal = 0
    let totalCuTva = 0

    lines.forEach((line) => {
      const lineTotal = line.quantity * line.unitPriceWithoutVat
      const lineTva = (lineTotal * (line.vatValue || 0)) / 100
      totalFaraTva += lineTotal
      tvaTotal += lineTva
      totalCuTva += lineTotal + lineTva
    })

    return {
      totalFaraTva: parseFloat(totalFaraTva.toFixed(2)),
      tvaTotal: parseFloat(tvaTotal.toFixed(2)),
      totalCuTva: parseFloat(totalCuTva.toFixed(2))
    };
  }

  const computeTotalDifferences = (values, document) => {
    const { totalFaraTva, tvaTotal, totalCuTva } = computeTotals(values.documentLines);
    const {
      totalValueWithoutVat,
      totalVatValue,
      totalValue
    } = document.data.documentTotal;

    const differences = [
      Math.abs(totalFaraTva - totalValueWithoutVat),
      Math.abs(tvaTotal - totalVatValue),
      Math.abs(totalCuTva - totalValue)
    ];

    return differences.some(diff => parseFloat(diff.toFixed(2)) > 0.05);
  };

  const validationSchema = Yup.object().shape({
    documentDate: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value
      )
      .test('min-date', function (value) {
        if (!allowedMinDate || !value) return true

        const selectedDate = new Date(value)
        const minDate = new Date(allowedMinDate.getTime())

        selectedDate.setHours(0, 0, 0, 0)
        minDate.setHours(0, 0, 0, 0)

        return selectedDate.getTime() >= minDate.getTime()
      }),
    dueDate: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === '' ? null : value
      )
      .required(MESSAGE.MANDATORY_FIELD.label),
    notes: Yup.string()
      .nullable()
      .max(200, MESSAGE.MAX_NUMBER_CHARACTERS.label(200)),
    pointOfWorkTag: Yup.string()
      .nullable()
      .required(MESSAGE.MANDATORY_FIELD.label),
    // invoiceGroup: Yup.string()
    //     .nullable()
    //     .required(MESSAGE.MANDATORY_FIELD.label),
    documentLines: Yup.array()
      .when('invoiceGroup',{
        is: value => ['Upfit', 'Pending'].includes(value),
        then: schema => schema.of(Yup.object()).min(0),
        otherwise: schema => schema.of(
          Yup.object().shape({
            article: Yup.string()
              .nullable()
              .required(MESSAGE.MANDATORY_FIELD.label),
            details: Yup.string()
              .nullable()
              .max(200, MESSAGE.MAX_NUMBER_CHARACTERS.label(200)),
            quantity: Yup.number()
              .notOneOf([0], MESSAGE.NON_NULL_QUANTITIY.label)
              .required(MESSAGE.MANDATORY_FIELD.label),
            unitPriceWithoutVat: Yup.number()
              .min(0, MESSAGE.NOT_NEGATIVE_PRICE.label)
              .required(MESSAGE.MANDATORY_FIELD.label),
            vatValue: Yup.number().required(MESSAGE.MANDATORY_FIELD.label).nullable(),
            unit: Yup.string().required(MESSAGE.MANDATORY_FIELD.label).nullable(),
            // objectNumber: Yup.string().required(MESSAGE.MANDATORY_FIELD.label),
            costCenter: Yup.string().required(MESSAGE.MANDATORY_FIELD.label),
            // expenseGroup: Yup.string()
            //     .required(MESSAGE.MANDATORY_FIELD.label)
            //     .nullable()
          })
        )
      })
      .min(1, MESSAGE.AT_LEAST_ONE_LINE.label)
  })

  const renderLeftSide = () => (
    <Box
      flex="0 0 40%"
      maxHeight="100%"
      overflow="hidden"
      borderRight="1px solid #e2e8f0"
      bg="gray.100"
      position="relative"
    >
      <Box height="100%" overflow="auto" p="1">
        {loadingPdf ? (
          <Flex justify="center" align="center" height="100%">
            <Spinner size="xl"/>
          </Flex>
        ) : pdfBlob ? (
          <Document
            key={pdfBlob}
            file={pdfBlob}
            onLoadSuccess={({numPages}) => setNumPages(numPages)}
            onLoadError={(error) => console.error('Error loading PDF:', error)}
          >
            {Array.from(new Array(numPages), (_, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                scale={zoomLevel}
              />
            ))}
          </Document>
        ) : (
          <Text>{MESSAGE.PDF_EXPORT.error}</Text>
        )}
      </Box>

      <Box
        position="absolute"
        bottom="10px"
        left="50%"
        transform="translateX(-50%)"
        zIndex="10"
        bg="white"
        p="2"
        borderRadius="md"
        boxShadow="lg"
        display="flex"
        gap="2"
      >
        <IconButton
          aria-label={LABEL.ZOOM.out}
          icon={<FaMinus/>}
          onClick={handleZoomOut}
        />
        <IconButton
          aria-label={LABEL.ZOOM.reset}
          icon={<FaRedo/>}
          onClick={handleResetZoom}
        />
        <IconButton
          aria-label={LABEL.ZOOM.in}
          icon={<FaPlus/>}
          onClick={handleZoomIn}
        />
      </Box>
    </Box>
  )

  const renderPreviousDocumentsTable = (previousDocuments) => {
    if (!previousDocuments || previousDocuments.length === 0) {
      return null;
    }

    return (
      <Box bg="white" p={4}>
        <Text borderBottom="1px solid" borderColor="gray.200" mb={2}>
          {LABEL.PREVIOUS_INVOICE_REFERENCE.label}
        </Text>
        <PreviousDocumentsTable previousDocuments={previousDocuments}/>
      </Box>
    )
  }

  const renderRightSide = () => {
    return (
      <Box
        flex="0 0 60%"
        maxHeight="100%"
        overflowY="auto"
        display="flex"
        flexDirection="column"
        position="relative"
      >
        <Formik
          initialValues={documentData}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={async (values, actions) => {
            if (computeTotalDifferences(values, document) && values.invoiceGroup !== 'Upfit' && values.invoiceGroup !== 'Pending') {
              setShowTotalDifferencesAlertBox(true);
            } else {
              await processAndSubmitDocument(values, actions)
            }
          }
          }
        >
          {({errors, values, setFieldValue, handleSubmit, isSubmitting, validateForm}) => {
            const handleFormSubmit = async (e) => {
              e.preventDefault();

              // Manually trigger validation and wait for it to complete
              const validationErrors = await validateForm();

              // Check if there are validation errors
              if (Object.keys(validationErrors).length > 0) {
                // Show the validation alert
                setShowValidationAlert(true);
              }
              handleSubmit(e);

            };

            return (
              <>

                <CaptureSetFieldValue setExternalSetFieldValue={setExternalSetFieldValue}/>
                <form
                  onSubmit={(e) => handleFormSubmit(e)}
                >
                  <Box bg="gray.50" p="4" flex="1" overflowY="auto">
                    <Flex gap="10">
                      {renderOrganisation()}
                      {renderPartner()}
                    </Flex>


                    {renderInvoiceDetails()}
                    {renderPreviousDocumentsTable(document.data.previousDocuments)}
                    {renderArticles({values, setFieldValue})}
                    {renderDocumentTotalAlertTable(values)}
                  </Box>

                  {renderBottomActionBar(isSubmitting, processing, values.invoiceGroup)}
                </form>
              </>
            );
          }}
        </Formik>
      </Box>
    )
  }

  const renderOrganisation = () => (
    <Box w="full">
      <Text
        color="gray.400"
        textTransform="uppercase"
        borderBottom="1px solid"
        borderColor="gray.300"
        fontSize="sm"
        fontWeight="100"
        mb={2}
      >
        {LABEL.ORGANISATION_PARTNER_DETAILS.buyer}
      </Text>
      <VStack align="start">
        <HStack>
          <Text textColor="gray.500" mb="0" fontSize="sm">
            {LABEL.ORGANISATION_PARTNER_DETAILS.name}
          </Text>
          <Text>{documentData.organisation.name}</Text>
        </HStack>
        <HStack>
          <Text textColor="gray.500" mb="0" fontSize="sm">
            {LABEL.ORGANISATION_PARTNER_DETAILS.cif}
          </Text>
          <Text>{documentData.organisation.cif}</Text>
        </HStack>

        <FastField name="pointOfWorkTag">
          {({field, meta, form}) => (
            <FormControl isInvalid={meta.touched && meta.error}>
              <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                {LABEL.POW_DROPDOWN.label}
              </FormLabel>
              {/* <PaginatedDropdown
                                                    value={field.value || ''}
                                                    fetchData={getPointOfWorks}
                                                    onChange={(value) => form.setFieldValue(field.code, value)}
                                                    label={LABEL.POW_DROPDOWN.label}
                                                    placeholder={LABEL.POW_DROPDOWN.placeholder}
                                                    cifValue={cif}
                                                /> */}
              <PointOfWorksDropdown
                value={field.value}
                cifValue={cif}
                onChange={(newCode) => form.setFieldValue(field.name, newCode)}
              />
              {meta.touched && meta.error && (
                <Text color="red.500" fontSize="sm">
                  {meta.error}
                </Text>
              )}
            </FormControl>
          )}
        </FastField>
      </VStack>
    </Box>
  )

  const renderPartner = () => {
    const {partner} = documentData
    const addressParts = partner.address
      ? [
        partner.address.addressLine,
        partner.address.city,
        partner.address.county,
        partner.address.postcode,
        partner.address.country
      ].filter(Boolean)
      : []

    return (
      <Box w="full">
        <Text
          color="gray.400"
          textTransform="uppercase"
          borderBottom="1px solid"
          borderColor="gray.300"
          fontSize="sm"
          fontWeight="100"
          mb={2}
        >
          {LABEL.ORGANISATION_PARTNER_DETAILS.seller}
        </Text>
        <VStack align="start" spacing={0}>
          <HStack>
            <Text textColor="gray.500" mb="0" fontSize="sm">
              {LABEL.ORGANISATION_PARTNER_DETAILS.name}
            </Text>
            <Text>{documentData.partner.name}</Text>
          </HStack>
          <HStack>
            <Text textColor="gray.500" mb="0" fontSize="sm">
              {LABEL.ORGANISATION_PARTNER_DETAILS.cif}
            </Text>
            <Text>{documentData.partner.cif}</Text>
          </HStack>
          <HStack>
            <Text textColor="gray.500" mb="0" fontSize="sm">
              {LABEL.ORGANISATION_PARTNER_DETAILS.group}
            </Text>
            <Text>{document.data.partner.partnerGroup}</Text>
          </HStack>
          {addressParts.length > 0 && (
            <HStack>
              <Text textColor="gray.500" mb="0" fontSize="sm">
                {LABEL.ORGANISATION_PARTNER_DETAILS.address}
              </Text>
              <Text>{addressParts.join(', ')}</Text>
            </HStack>
          )}
        </VStack>
      </Box>
    )
  }

  const renderArticles = ({values, setFieldValue}) => {
    if(values.invoiceGroup === 'Upfit' || values.invoiceGroup === 'Pending') return null

    const lineCount = values.documentLines?.length ?? 0;
    const useReadOnlyVirtualTable = lineCount > 25;

    if(useReadOnlyVirtualTable) {
      return (
        <DocumentLinesReadOnlyTable
        lines={values.documentLines}
        />
      )
    }
    return(
      <Box>
        <Text borderBottom="1px solid" borderColor="gray.200" mb={2} p={2} pl={0}>
          Listă articole
        </Text>
        <FieldArray name="documentLines">
          {({remove, push}) => (
            <Box>
              {values.documentLines &&
                values.documentLines.map((line, index) => {
                  const expenseGroupName = line.expenseGroup || ''
                  const expenseGroupId = `${cif}_${expenseGroupName}`
                  return <Box
                    key={index}
                    borderWidth="1px"
                    borderRadius="lg"
                    bg="white"
                    mb="5"
                    p="2"
                    shadow="sm"
                  >
                    <Flex justify="space-between" alignItems="center" mb="4">
                      <Text>Articol - {index + 1}</Text>
                      {!user.user.isGuest &&(
                        <Button
                          colorScheme="red"
                          size="sm"
                          aria-label={LABEL.LINE_BUTTONS.deleteLine}
                          disabled={values.documentLines.length <= 1}
                          onClick={() => remove(index)}
                        >
                          <FaTrash/>
                        </Button>
                      )}
                    </Flex>

                    <FastField name={`documentLines[${index}].details`}>
                      {({field, meta, form}) => (
                        <FormControl
                          isInvalid={
                            (meta.touched || form.submitCount > 0) && meta.error
                          }
                        >
                          <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                            {LABEL.LINE_DETAILS.description}
                          </FormLabel>
                          <Textarea
                            border='1px'
                            borderColor='black'
                            {...field}
                            bg="white"
                            resize="vertical"
                            size="sm"
                          />
                          {(meta.touched || form.submitCount > 0) &&
                            meta.error && (
                              <FormErrorMessage>{meta.error}</FormErrorMessage>
                            )}
                        </FormControl>
                      )}
                    </FastField>

                    <Flex gap="5">

                      {cif && !loadingProjects && (
                        <FastField name={`documentLines[${index}].expenseGroup`}>
                          {({field, form, meta}) => (
                            <FormControl
                              isInvalid={
                                (meta.touched || form.submitCount > 0) && meta.error
                              }
                            >
                              <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                                {LABEL.EXPENSE_GROUP_DROPDOWN.label}
                              </FormLabel>
                              <ExpenseGroupsDropDown
                                value={field.value || ''}
                                expenseGroups={expenseGroups}
                                loading={loadingExpenseGroups}
                                onChange={(value) =>{
                                  form.setFieldValue(field.name, value)
                                  form.setFieldValue(`documentLines[${index}].article`,'')
                                }}
                              />
                              {(meta.touched || form.submitCount > 0) &&
                                meta.error && (
                                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                                )}
                            </FormControl>
                          )}
                        </FastField>
                      )}

                      <FastField name={`documentLines[${index}].article`} key={`article-${expenseGroupId}`}>
                        {({field, form, meta}) => (
                          <FormControl
                            isInvalid={
                              (meta.touched || form.submitCount > 0) && meta.error
                            }
                          >
                            <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                              {LABEL.LINE_DETAILS.article}
                            </FormLabel>
                            <PaginatedDropdown
                              value={field.value || ''}
                              fetchData={getArticles}
                              onChange={(value) => form.setFieldValue(field.name, value)}
                              label={LABEL.ARTICLE_DROPDOWN.label}
                              placeholder={LABEL.ARTICLE_DROPDOWN.placeholder}
                              cifValue={cif}
                              expenseGroup={expenseGroupId}
                            />
                            {(meta.touched || form.submitCount > 0) &&
                              meta.error && (
                                <FormErrorMessage>{meta.error}</FormErrorMessage>
                              )}
                          </FormControl>
                        )}
                      </FastField>
                    </Flex>

                    <Flex gap="5">
                      <FastField name={`documentLines[${index}].unit`}>
                        {({field, meta, form}) => (
                          <FormControl
                            isInvalid={
                              (meta.touched || form.submitCount > 0) && meta.error
                            }
                          >
                            <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                              {LABEL.LINE_DETAILS.measureUnit}
                            </FormLabel>
                            <Input
                              bg="white"
                              size="sm"
                              borderRadius="lg"
                              {...field}
                              border='1px'
                              borderColor='black'
                            />
                            {(meta.touched || form.submitCount > 0) &&
                              meta.error && (
                                <FormErrorMessage>{meta.error}</FormErrorMessage>
                              )}
                          </FormControl>
                        )}
                      </FastField>

                      <FastField name={`documentLines[${index}].quantity`}>
                        {({field, meta, form}) => (
                          <FormControl
                            isInvalid={
                              (meta.touched || form.submitCount > 0) && meta.error
                            }
                          >
                            <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                              {LABEL.LINE_DETAILS.quantity}
                            </FormLabel>
                            <Input
                              {...field}
                              type="number"
                              bg="white"
                              size="sm"
                              borderRadius="lg"
                              border='1px'
                              borderColor='black'
                            />
                            {(meta.touched || form.submitCount > 0) &&
                              meta.error && (
                                <FormErrorMessage>{meta.error}</FormErrorMessage>
                              )}
                          </FormControl>
                        )}
                      </FastField>

                      <FastField
                        name={`documentLines[${index}].unitPriceWithoutVat`}
                      >
                        {({field, meta, form}) => (
                          <FormControl
                            isInvalid={
                              (meta.touched || form.submitCount > 0) && meta.error
                            }
                          >
                            <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                              {LABEL.LINE_DETAILS.unitPriceWithoutVat}
                            </FormLabel>
                            <Input
                              {...field}
                              type="number"
                              bg="white"
                              size="sm"
                              borderRadius="lg"
                              border='1px'
                              borderColor='black'
                            />
                            {(meta.touched || form.submitCount > 0) &&
                              meta.error && (
                                <FormErrorMessage>{meta.error}</FormErrorMessage>
                              )}
                          </FormControl>
                        )}
                      </FastField>

                      <FastField name={`documentLines[${index}].vatValue`}>
                        {({field, meta, form}) => (
                          <FormControl
                            isInvalid={
                              (meta.touched || form.submitCount > 0) && meta.error
                            }
                          >
                            <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                              {LABEL.LINE_DETAILS.vatPercentage}
                            </FormLabel>
                            <VatDropDown
                              value={field.value}
                              onChange={(value) =>
                                form.setFieldValue(field.name, value)
                              }
                            />
                            {(meta.touched || form.submitCount > 0) &&
                              meta.error && (
                                <FormErrorMessage>{meta.error}</FormErrorMessage>
                              )}
                          </FormControl>
                        )}
                      </FastField>
                    </Flex>

                    <Flex gap="5">
                      {cif && !loadingProjects && (
                        <FastField name={`documentLines[${index}].costCenter`}>
                          {({field, form, meta}) => (
                            <FormControl
                              isInvalid={
                                (meta.touched || form.submitCount > 0) && meta.error
                              }
                            >
                              <CostCenterDropDown
                                value={field.value}
                                costCenters={costCenters}
                                loading={loadingCostCenters}
                                onChange={(value) =>
                                  form.setFieldValue(field.name, value)
                                }
                              />
                              {(meta.touched || form.submitCount > 0) &&
                                meta.error && (
                                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                                )}
                            </FormControl>
                          )}
                        </FastField>
                      )}

                      {cif && !loadingProjects && (
                        <FastField  name={`documentLines[${index}].objectNumber`}>
                          {({field, form, meta}) => (
                            <FormControl
                              isInvalid={
                                (meta.touched || form.submitCount > 0) && meta.error
                              }
                            >
                              <ProjectsDropDown
                                value={field.value}
                                projects={projects}
                                loading={loadingProjects}
                                onChange={(value) =>
                                  form.setFieldValue(field.name, value)
                                }
                              />
                              {(meta.touched || form.submitCount > 0) &&
                                meta.error && (
                                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                                )}
                            </FormControl>
                          )}
                        </FastField>
                      )}
                    </Flex>

                    <Flex gap={5}>
                      <FastField
                        name={`documentLines[${index}].invoicePeriodStartDate`}
                      >
                        {({field, form}) => (
                          <FormControl>
                            <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                              {LABEL.LINE_DETAILS.invoicePeriodStartDate}
                            </FormLabel>
                            <Input
                              bg="white"
                              type="date"
                              size="sm"
                              borderRadius="lg"
                              value={field.value ? formatDate(field.value) : ''}
                              border='1px'
                              borderColor='black'
                              onChange={(e) =>
                                form.setFieldValue(
                                  field.name,
                                  new Date(e.target.value).getTime()
                                )
                              }
                            />
                          </FormControl>
                        )}
                      </FastField>

                      <FastField
                        name={`documentLines[${index}].invoicePeriodEndDate`}
                      >
                        {({field, form}) => (
                          <FormControl>
                            <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                              {LABEL.LINE_DETAILS.invoicePeriodEndDate}
                            </FormLabel>
                            <Input
                              bg="white"
                              type="date"
                              size="sm"
                              borderRadius="lg"
                              value={field.value ? formatDate(field.value) : ''}
                              border='1px'
                              borderColor='black'
                              onChange={(e) =>
                                form.setFieldValue(
                                  field.name,
                                  new Date(e.target.value).getTime()
                                )
                              }
                            />
                          </FormControl>
                        )}
                      </FastField>
                    </Flex>
                  </Box>
                })}
              <Flex justify="end">

                {!user.user.isGuest &&(

                  <Button
                    mt="2"
                    colorScheme="green"
                    leftIcon={<FaPlus/>}
                    onClick={() =>
                      push({
                        details: '',
                        costCenter: '',
                        expenseGroup: '',
                        article: '',
                        objectNumber: '',
                        unit: '',
                        quantity: 0,
                        unitPriceWithoutVat: 0,
                        vatValue: 0,
                        invoicePeriodStartDate: '',
                        invoicePeriodEndDate: '',
                        notes: ''
                      })
                    }
                  >
                    {LABEL.ADD_LINE.label}
                  </Button>
                )}
              </Flex>
            </Box>
          )}
        </FieldArray>
      </Box>
    )}

  const renderBottomActionBar = (isSubmitting, processing, invoiceGroup) => {
    const commonButtonProps = (icon, colorScheme, label) => ({
      type: "submit",
      colorScheme,
      isLoading: isSubmitting || processing,
      leftIcon: icon,
      children: label,
    });
    const renderButtons = () => {
      switch (status) {
        case DOCUMENT_STATUS.PROCESSED.key:
          return (
            <>
              <Flex justify="space-between" width="100%">
              {(!user.user.isGuest) && (
                <Button {...commonButtonProps(<FaPencil />, "blue", LABEL.PROCESS_BUTTONS.updateDocument)} />
              )}
            </Flex>
              {!user.user.isGuest && !user.user.isOperational && invoiceGroup !== 'Upfit' && invoiceGroup !== 'Pending' &&(
                <Button
                  colorScheme="green"
                  onClick={handleApproveDocument}
                  isLoading={processing}
                  leftIcon={<FaCheck />}
                >
                  {LABEL.PROCESS_BUTTONS.approveDocument}
                </Button>
              )}
            </>
          );
        case DOCUMENT_STATUS.APPROVED.key:
          return (
            <Flex justify="space-between" width="100%">
              {(user.user.isAdmin || user.user.isController || user.user.isManager) && (
                <Button {...commonButtonProps(<FaPencil />, "blue", LABEL.PROCESS_BUTTONS.updateDocument)} />
              )}
              {!user.user.isGuest && !user.user.isOperational && invoiceGroup !== 'Upfit' && invoiceGroup !== 'Pending' && (
                <Button
                  colorScheme="red"
                  onClick={handleSendToOMC}
                  isLoading={processing}
                  leftIcon={<FaPaperPlane />}
                >
                  {LABEL.PROCESS_BUTTONS.sendToOMC}
                </Button>
              )}
            </Flex>
          );
        case DOCUMENT_STATUS.SENT_TO_OMC.key:
          return (
            <Flex justify="space-between" width="100%">
              {(user.user.isAdmin || user.user.isController) && (
                <Button {...commonButtonProps(<FaPencil />, "blue", LABEL.PROCESS_BUTTONS.updateDocument)} />
              )}
            </Flex>
          );
        default:
          return (
            <Flex justify="space-between" width="100%">
              {(!user.user.isGuest) && (
                <Button {...commonButtonProps(<FaPlay />, "blue", LABEL.PROCESS_BUTTONS.processDocument)} />
              )}
            </Flex>
          );
      }
    };

    return (
      <Box
        position="sticky"
        bottom="0"
        left="0"
        bg="rgba(255, 255, 255, 0.2)"
        backdropFilter="blur(5px)"
        p={2}
        borderTop="1px solid"
        borderColor="gray.300"
      >
        <Flex
          alignItems="center"
          justify="flex-end"
          gap={2}
          w="100%"
        >
          {renderButtons()}
        </Flex>
      </Box>
    );
  };

  const renderInvoiceDetails = () => (
    <Box>
      <Text borderBottom="1px solid" borderColor="gray.200" mb={2} p={2} pl={0}>
        Detalii document
      </Text>
      <Grid templateColumns="repeat(3, 1fr)" gap={2} m="0" mb="5">
        <Box>
          <HStack>
            <Text textColor="gray.500" mb="0" fontSize="sm">Nr. Document</Text>
            <Text>{documentData.series}</Text>
          </HStack>
        </Box>

        <Box>
          <HStack>
            <Text textColor="gray.500" mb="0" fontSize="sm">Dată Emitere</Text>
            <Text>{formatDate(documentData.issueDate)}</Text>
          </HStack>
        </Box>

      </Grid>


      <Grid templateColumns="repeat(2, 1fr)" gap={2} m="0" p="0">
        <FastField name="documentDate">
          {({ field, meta, form }) => (
            <DocumentDateField
              allowedMinDate={allowedMinDate}
              documentData={documentData}
              field={field}
              meta={meta}
              form={form}
            />
          )}
        </FastField>

        <FastField name="dueDate">
          {({ field, meta, form }) => (
            <FormControl isInvalid={meta.touched && meta.error}>
              <FormLabel textColor="gray.500" mb="0" fontSize="sm">
                {LABEL.INVOICE_DETAILS.dueDate}
              </FormLabel>
              <Input
                {...field}
                bg="white"
                borderRadius="lg"
                size="sm"
                border='1px'
                borderColor='black'
                type="date"
                value={field.value ? formatDate(field.value) : ''}
                onChange={(e) => form.setFieldValue(field.name, new Date(e.target.value).getTime())}
              />
              {meta.touched && meta.error && (
                <FormErrorMessage>{meta.error}</FormErrorMessage>
              )}
            </FormControl>
          )}
        </FastField>

        {cif && !loadingGroups && (
          <FastField name="invoiceGroup">
            {({field, meta, form}) => (
              <FormControl isInvalid={meta.touched && meta.error}>
                <GroupsDropDown
                  value={field.value}
                  groups={groups}
                  loading={loadingGroups}
                  onChange={(value) => form.setFieldValue(field.name, value)}
                />
                {(meta.touched || form.submitCount > 0) && meta.error && (
                  <Text color="red.500" fontSize="sm">
                    {meta.error}
                  </Text>
                )}
              </FormControl>
            )}
          </FastField>
        )}

      </Grid>

      <FastField name="notes">
        {({field, meta}) => (
          <FormControl mb="2" isInvalid={meta.touched && meta.error}>
            <FormLabel textColor="gray.500" mb="0" fontSize="sm">
              {LABEL.LINE_DETAILS.observations}
            </FormLabel>
            <Textarea {...field} bg="white" borderRadius="lg" size="sm" border='1px' borderColor='black'/>
            {meta.touched && meta.error && (
              <FormErrorMessage>{meta.error}</FormErrorMessage>
            )}
          </FormControl>
        )}
      </FastField>
    </Box>
  )

  const renderTotalDifferencesAlertBox = () => {
    return (
      <AlertDialog isOpen={showTotalDifferencesAlertBox}
                   onClose={() => setShowTotalDifferencesAlertBox(false)} zIndex="2000">
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {MESSAGE.PRICE_DIFFERENCE_ERRORS.title}
            </AlertDialogHeader>

            <AlertDialogBody>
              <Flex alignItems="center">
                <Icon as={FaExclamation} w={12} h={12} color="red.500" mr={4}/>
                <span>{MESSAGE.PRICE_DIFFERENCE_ERRORS.description}</span>
              </Flex>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button colorScheme="blue" onClick={() => setShowTotalDifferencesAlertBox(false)}>
                {MESSAGE.PRICE_DIFFERENCE_ERRORS.button}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    )
  }

  const renderValidationErrorMessageBar = (
    description = MESSAGE.GENERIC_ERROR.description) => {
    return (
      <Alert status="error" p="5" mb="2">
        <AlertIcon/>
        <AlertTitle>{MESSAGE.GENERIC_ERROR.title}</AlertTitle>
        <AlertDescription>{description}</AlertDescription>
        <CloseButton position="absolute" right="8px" top="5px"
                     onClick={() => setShowValidationAlert(false)}/>
      </Alert>
    )
  }

  const renderDocumentTotalAlertTable = (values) => {
    const {totalFaraTva, tvaTotal, totalCuTva} = computeTotals(values.documentLines);

    const {
      totalValueWithoutVat: defaultTotalValueWithoutVat,
      totalVatValue: defaultTotalVatValue,
      totalValue: defaultTotalValue
    } = document.data.documentTotal;

    const getRowColor = (difference) => (Math.abs(difference) <= 0.05 ? "green.200" : "red.200");

    if(values.invoiceGroup === 'Upfit' || values.invoiceGroup === 'Pending') return null

    return (
      <Box>
        <Text borderBottom="1px solid" borderColor="gray.200" mb={2} p={2} pl={0}>
          Total document
        </Text>
        <Table backgroundColor="white" variant="simple" borderWidth="1px" borderColor="gray.200" size="sm">
          <Thead>
            <Tr>
              <Th></Th>
              <Th>{LABEL.PRICE_DIFFERNECE_TABLE.document}</Th>
              <Th>{LABEL.PRICE_DIFFERNECE_TABLE.processedDocument}</Th>
              <Th>{LABEL.PRICE_DIFFERNECE_TABLE.difference}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {[
              {
                label: LABEL.TVA_TOTALS.totalWithoutVat,
                spv: defaultTotalValueWithoutVat,
                omc: totalFaraTva
              },
              {
                label: LABEL.TVA_TOTALS.vatTotal,
                spv: defaultTotalVatValue,
                omc: tvaTotal
              },
              {
                label: LABEL.TVA_TOTALS.totalWithVat,
                spv: defaultTotalValue,
                omc: totalCuTva
              }
            ].map(({label, spv, omc}, index) => {
              const difference = parseFloat((omc - spv).toFixed(2));
              return (
                <Tr key={index} backgroundColor={getRowColor(difference)}>
                  <Td>{label}</Td>
                  <Td>{spv}</Td>
                  <Td>{omc}</Td>
                  <Td>{difference}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  };

  if (loadingData || !documentData) {
    return (
      <Flex justify="center" align="center" height="100vh">
        <Spinner size="xl"/>
      </Flex>
    )
  }

  return (
    <Flex flexDirection="column" height="100%">
      {document && cif && <DocumentMetaData document={document} documentData={documentData} setFieldValue={externalSetFieldValue} costCenters={costCenters} expenseGroups={expenseGroups.map(expenseGroup => expenseGroup.name)} user={user} projects={projects} cif={cif}/>}
      {showTotalDifferencesAlertBox && renderTotalDifferencesAlertBox()}
      {showValidationAlert && renderValidationErrorMessageBar(errorDescription)}
      <Flex maxHeight="88%">
        {renderLeftSide()}
        {renderRightSide()}
      </Flex>
    </Flex>
  )
}

export default DocumentDetailsPage
